/**
 * Overlays — [T149-PR8-CONT-11] state overlays rendered inside the body region.
 *
 * Priority: error/compat > timeout > no-data (only one overlay renders at a time).
 * The compat branch renders `CompatibilityWarning` (full) inside an
 * `incompatible` testid wrapper when `compatError.code?.startsWith('COMPAT_')`
 * is true; otherwise it falls back to a generic `AlertCircle` error banner.
 *
 * The `ViewModeRecovery` button cluster is reused by both the compat-error
 * and the generic-error branches. It owns the "confirm remove" two-step
 * flow via the `pendingRemove` boolean.
 */

import { useState } from 'react';
import { AlertCircle, RefreshCw, Inbox, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompatibilityWarning } from '@/components/dashboard/CompatibilityWarning';
import type { BodyState, ViewModeRecoveryProps } from './types';

/**
 * ViewModeRecovery — Fix / Retry / Remove button cluster with a
 * two-step remove confirmation. Lives here because it is shared
 * between both the compat-error and the generic-error overlays.
 */
function ViewModeRecovery({
  id,
  pendingRemove,
  onPendingRemove,
  onRetry,
  onRequestViewModeFix,
  onRequestRemove,
}: ViewModeRecoveryProps) {
  if (pendingRemove) {
    return (
      <div className="flex flex-wrap items-center gap-2 justify-center">
        <span className="text-xs text-muted-foreground">Remove widget?</span>
        <Button
          variant="destructive"
          size="sm"
          className="min-h-[44px]"
          onClick={() => {
            onRequestRemove?.(id);
            onPendingRemove(false);
          }}
        >
          Confirm
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          onClick={() => onPendingRemove(false)}
        >
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 justify-center">
      {onRequestViewModeFix && (
        <Button
          data-testid="widget-fix-btn"
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          onClick={() => onRequestViewModeFix(id)}
        >
          Fix
        </Button>
      )}
      {onRetry && (
        <Button
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          onClick={onRetry}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Retry
        </Button>
      )}
      {onRequestRemove && (
        <Button
          variant="outline"
          size="sm"
          className="min-h-[44px]"
          onClick={() => onPendingRemove(true)}
        >
          Remove
        </Button>
      )}
    </div>
  );
}

/**
 * The single active overlay to render above the body content.
 * `pendingRemove` is owned here (extracted from the parent component)
 * so the body slot can be a pure presentational composition.
 */
export function ActiveOverlay(body: BodyState) {
  const {
    id,
    title,
    errorMessage,
    compatError,
    onRetry,
    onRequestFix,
    isNoData,
    noDataMessage,
    isTimeout,
    isViewMode,
    onRequestViewModeFix,
    onRequestRemove,
  } = body;

  // `pendingRemove` is local to the overlay so the two-step confirm
  // flow never causes the parent to re-render.
  const [pendingRemove, setPendingRemove] = useState(false);

  if (errorMessage) {
    if (compatError?.code?.startsWith('COMPAT_')) {
      return (
        <div
          data-testid="widget-error-message"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 bg-background/80 backdrop-blur-sm z-10"
        >
          <div
            data-testid="widget-state-incompatible"
            className="flex flex-col items-center gap-2 w-full"
          >
            <CompatibilityWarning
              widgetId={id}
              error={compatError}
              onRequestFix={onRequestFix ?? (() => {})}
            />
            {isViewMode && (
              <ViewModeRecovery
                id={id}
                pendingRemove={pendingRemove}
                onPendingRemove={setPendingRemove}
                onRetry={onRetry}
                onRequestViewModeFix={onRequestViewModeFix}
                onRequestRemove={onRequestRemove}
                title={title}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        data-testid="widget-error-message"
        className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 bg-background/80 backdrop-blur-sm z-10"
      >
        <div
          data-testid="widget-state-error"
          className="flex flex-col items-center gap-2 w-full"
        >
          <AlertCircle
            className="h-5 w-5 text-destructive shrink-0"
            aria-hidden="true"
          />
          {/* Always-visible error message — no expand required */}
          <p
            className="text-xs text-muted-foreground line-clamp-2 mt-1"
            data-testid="widget-error-detail"
          >
            {errorMessage}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {onRetry && (
              <Button
                data-testid={`widget-retry-${id}`}
                variant="outline"
                size="sm"
                onClick={onRetry}
                aria-label={`Retry loading ${title}`}
                className="min-h-[44px]"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                Retry
              </Button>
            )}
            {isViewMode && (
              <ViewModeRecovery
                id={id}
                pendingRemove={pendingRemove}
                onPendingRemove={setPendingRemove}
                onRetry={undefined}
                onRequestViewModeFix={onRequestViewModeFix}
                onRequestRemove={onRequestRemove}
                title={title}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isTimeout) {
    return (
      <div
        data-testid="widget-state-timeout"
        className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 bg-background/80 backdrop-blur-sm z-10"
      >
        <Clock className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
        <p className="text-xs text-center text-muted-foreground">
          ⏱ Couldn&apos;t load data
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onRetry && (
            <Button
              data-testid={`widget-retry-${id}`}
              variant="outline"
              size="sm"
              onClick={onRetry}
              aria-label={`Retry loading ${title}`}
              className="min-h-[44px]"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              Retry
            </Button>
          )}
          {isViewMode && (
            <ViewModeRecovery
              id={id}
              pendingRemove={pendingRemove}
              onPendingRemove={setPendingRemove}
              onRetry={undefined}
              onRequestViewModeFix={onRequestViewModeFix}
              onRequestRemove={onRequestRemove}
              title={title}
            />
          )}
        </div>
      </div>
    );
  }

  if (isNoData) {
    return (
      <div
        data-testid="widget-state-no-data"
        className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 bg-background/80 backdrop-blur-sm z-10"
      >
        <Inbox className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
        <p className="text-xs text-center text-muted-foreground break-words">
          {noDataMessage ?? 'No data available for this period'}
        </p>
        {onRetry && (
          <Button
            data-testid={`widget-retry-${id}`}
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="min-h-[44px]"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Retry
          </Button>
        )}
      </div>
    );
  }

  return null;
}
